import React, { useState, useEffect } from "react";
import { Spin, Result, Button } from "antd";
import { RocketOutlined } from "@ant-design/icons";
import { getProvisionStatus } from "../../services/polly/llmSettingsService";

/**
 * Wraps Polly pages with a provisioning check.
 * If the customer is provisioned, renders children.
 * If not, shows a contact screen.
 */
const PollyGate = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [provisioned, setProvisioned] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const checkProvisioning = async () => {
      try {
        const status = await getProvisionStatus();

        if (cancelled) return;

        // Check if customer_llm_setting exists (not "missing")
        const llmSettingStatus = status?.provisions?.customer_llm_setting?.status;
        setProvisioned(llmSettingStatus && llmSettingStatus !== "missing");
      } catch (err) {
        if (!cancelled) {
          // 404 = customer not found = not provisioned (not an error)
          if (err?.response?.status === 404 || err?.message?.includes("404")) {
            setProvisioned(false);
          } else {
            console.error("Failed to check Polly provisioning:", err);
            setError(err);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    checkProvisioning();

    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
        <Spin size="large" tip="Checking Polly setup..." />
      </div>
    );
  }

  if (error) {
    return (
      <Result
        status="warning"
        title="Unable to verify Polly setup"
        subTitle="Please try again later or contact support."
      />
    );
  }

  if (!provisioned) {
    return (
      <Result
        icon={<RocketOutlined style={{ color: "#1890ff" }} />}
        title="Polly AI Conversations"
        subTitle={
          <div style={{ maxWidth: 500, margin: "0 auto", textAlign: "center" }}>
            <p style={{ fontSize: 16, marginBottom: 16 }}>
              Polly uses AI to have natural conversations with your customers,
              answer their questions, and collect valuable insights — all automatically.
            </p>
            <p style={{ fontSize: 14, color: "#666" }}>
              To get started with Polly, please contact the BluStream team and
              we'll help set everything up for your account.
            </p>
          </div>
        }
        extra={
          <Button
            type="primary"
            size="large"
            href="mailto:support@blustream.io?subject=Polly Setup Request"
          >
            Contact BluStream
          </Button>
        }
      />
    );
  }

  return children;
};

export default PollyGate;
